'use client';

import Image from 'next/image';
import Link from 'next/link';
import React, { useEffect } from 'react';
import { VariantProps } from 'tailwind-variants';

import { eventBannerHero } from '@/fo/ui/kit/banner/hero';
import {
  UiKitBreadcrumbsBase,
  UiKitBreadcrumbsBaseProps
} from '@/fo/ui/kit/breadcrumbs/base';
import { useMediaQuery } from '@/fo/ui/mod/media/query';
import { utilScrollToElement } from '@/fo/util/scroll-to-element';

import { styles } from './UiKitBannerAboutVideo.styles';

type NativeElement = React.HTMLAttributes<HTMLDivElement>;
type Variants = VariantProps<typeof styles>;

export interface Banner {
  alt?: string;
  label?: string;
  title?: string;
  subtitle?: string;
  video?: Video;
  image?: Image;
  description?: React.ReactNode;
  mode?: Variants['mode'];
}

export type Image = {
  sm: string;
  md: string;
};

export type Link = {
  icon: string;
  title: string;
  href: string;
};

export type Video = {
  desktop: string;
  mobile: string;
};

export interface UiKitBannerAboutVideoProps {
  banner: Banner;
  links?: Link[];
  breadcrumbs?: UiKitBreadcrumbsBaseProps;
  className?: string;
  mode?: Variants['mode'];
  onClick?: NativeElement['onClick'];
}

export function UiKitBannerAboutVideo({
  banner,
  breadcrumbs,
  mode,
  links,
  className,
  ...props
}: UiKitBannerAboutVideoProps) {
  const slots = styles({ className, mode });

  const scrollToSection = (id: string) => {
    const block = document.getElementById(id);

    utilScrollToElement(block);
  };

  useEffect(() => {
    if (mode) {
      eventBannerHero.setMode(mode);
    }

    return () => {
      eventBannerHero.setMode('off');
    };
  }, [banner.mode]); //eslint-disable-line

  const isMobile = useMediaQuery('lg', 'max');
  const videoSrc = isMobile ? banner.video?.mobile : banner.video?.desktop;

  return (
    <div
      className={slots.base()}
      {...props}
    >
      {banner.video && (
        <video
          className={slots.video()}
          autoPlay
          muted
          loop
          playsInline
          width={1440}
          height={400}
          src={videoSrc}
        />
      )}
      {banner.image && (
        <picture className={slots.imageContainer()}>
          <source
            srcSet={banner.image.sm}
            media="(max-width: 767px)"
          />
          <img
            className={slots.image()}
            src={banner.image.md}
            alt={banner.title}
          />
        </picture>
      )}
      <div className={slots.wrap()}>
        <div className={slots.breadcrumbsWrapper()}>
          {!!breadcrumbs && (
            <UiKitBreadcrumbsBase
              items={breadcrumbs.items}
              className={slots.breadcrumbs()}
            />
          )}
        </div>
        <div className={slots.body()}>
          <div className={slots.title()}>{banner.title}</div>
          <div className={slots.description()}>{banner.description}</div>
          {links && (
            <div className={slots.linkList()}>
              {links.map((link, index) => (
                <button
                  key={index}
                  className={slots.link()}
                  onClick={() => scrollToSection(link.href)}
                >
                  <div className={slots.linkBack()}></div>
                  <div className={slots.linkIconWrap()}>
                    <Image
                      src={link.icon}
                      className={slots.linkIcon()}
                      width={144}
                      height={144}
                      alt={`image-${index}`}
                    />
                  </div>
                  <div
                    className={slots.linkTitle()}
                    dangerouslySetInnerHTML={{ __html: link.title }}
                  />
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
